import type { Allocation, CateringItem, CateringLine, CateringMethod, CateringMode, CateringStage, Flavour, FlavourInfo, OrderDto, Protein } from '@tikkas/shared';
import { api } from './api.js';
import { uid } from './format.js';

/* ---------- what the API sends (apps/api/src/routes/catering.ts) ---------- */

export interface CateringMenu {
  items: CateringItem[];
  flavours: Record<Flavour, FlavourInfo>;
  rules: { deliveryMinimumPence: number; deliveryRadiusMiles: number; todayCutoff: string; tomorrowCutoff: string; collectionFrom: string; earnsPoints: boolean; dropsPerSlot: number };
  terms: string;
  site: { name: string; address: string };
  /** Google Places is wired up (otherwise the sample addresses stand in). */
  places: boolean;
}
export type SlotState = 'open' | 'last' | 'full';
export interface CateringDay { dateKey: string; lane: 'today' | 'tomorrow' | 'ahead'; label: string; long: string; open: boolean; cutoffAt: string; cutText: string; left: number | null; slots: { time: string; label: string; state: SlotState; booked: number }[] }
export interface PricedLine { id: string; itemId: string; name: string; kind: CateringItem['kind']; qty: number; unitPence: number; linePence: number; covers: number; allocations: Allocation[] }
export interface Totals { lines: PricedLine[]; subtotalPence: number; totalPence: number; vatPence: number; mains: number; boxes: number; minimumShortPence: number }
export interface Zone { inZone: boolean; miles: number; minutes: number; text: string; name: string; address: string }
export interface Quote { ok: boolean; issue: string | null; error?: string; lineId?: string | null; totals: Totals | null; zone: Zone | null; pointsToEarn: number; cutText: string | null; slotState: SlotState | null }
export interface Suggestion { placeId: string; name: string; address: string }
export interface PlaceDetail { place: { placeId: string; name: string; formatted: string; lat: number; lng: number }; zone: { inZone: boolean; miles: number; minutes: number; text: string } }
export interface SavedAddress { id: string; place_id: string; name: string; address: string; floor: string; access_notes: string; last_used_at: string | null }
export interface RosterEntry { name: string; requirement: string }
export interface TeamResponse { name: string; itemId: string; protein: string; flavour: string; note: string; at: string }
export interface TeamLink { token: string; path: string; deadlineAt: string; expected: number; status: 'open' | 'closed' | 'used'; company: string; dateKey: string; responses: TeamResponse[]; lines: CateringLine[] }

export interface CateringView {
  order: OrderDto;
  catering: {
    dateKey: string; dateLong: string; lane: string; cutoffAt: string; cutText: string; method: CateringMethod; slot: string | null; slotLabel: string | null; mode: CateringMode; headcount: number; mains: number; boxes: number;
    address: { name: string; address: string; floor: string; accessNotes: string; miles: number | null } | null;
    company: string; contactName: string; contactMobile: string; invoiceEmail: string; poNumber: string | null; notes: string;
    stage: CateringStage; stageLabel: string; timeline: { stage: CateringStage; label: string; at: string | null; done: boolean; now: boolean }[]; eta: string | null; refusedReason: string | null; invoiceNumber: string | null; teamLinkToken: string | null;
    policy: { window: 'full' | 'half' | 'none'; refundPence: number; label: string; text: string }; canChange: boolean; freeDropMains: number; paidPence: number; paidParts: number[];
  };
  lines: { id: string; itemId: string; name: string; qty: number; unitPence: number; linePence: number; allocations: { protein: string | null; flavour: string | null; qty: number; eaterName: string | null; dietaryNote: string | null }[] }[];
  requests: { id: string; kind: string; text: string; status: string; reply: string | null; refundPence: number; extraPence: number; extraOrderId: string | null; at: string; decidedAt: string | null }[];
  unreadMessages: number;
  /** Extra-cost changes waiting to be paid (small child orders). */
  extras?: { id: string; totalPence: number; status: string }[];
}
export interface ChatMessage { id: string; from: 'customer' | 'staff' | 'system'; author: string | null; text: string; at: string }

export const LIVE_STAGES: CateringStage[] = ['booked', 'kitchen', 'packed', 'out'];
export const isLive = (v: CateringView): boolean => LIVE_STAGES.includes(v.catering.stage);

/* ---------- the draft the buyer builds (kept on the device until paid) ---------- */

export interface Bowl { id: string; name: string; itemId: string; protein: Protein; flavour: Flavour; note: string }
export interface Draft {
  dateKey: string | null;
  method: CateringMethod;
  slot: string | null;
  place: { placeId: string; name: string; address: string; inZone: boolean; miles: number; text: string } | null;
  floor: string;
  accessNotes: string;
  mode: CateringMode | null;
  headcount: number;
  /** For a group: items with a flavour choice (meals, tikka trays): the quantity and its split by "Flavour|protein". */
  group: Record<string, { qty: number; alloc: Record<string, number> }>;
  bowls: Bowl[];
  /** Everything without a choice (rice and salad trays, sauce tubs, sides, desserts, drinks, pots): a quantity. */
  extras: Record<string, number>;
  team: { token: string } | null;
  company: string;
  contactName: string;
  contactMobile: string;
  invoiceEmail: string;
  poNumber: string;
  notes: string;
  reorderOf: string | null;
}
const KEY = 'tikkas-cater-v1';
export const emptyDraft = (): Draft => ({ dateKey: null, method: 'delivery', slot: null, place: null, floor: '', accessNotes: '', mode: null, headcount: 12, group: {}, bowls: [], extras: {}, team: null, company: '', contactName: '', contactMobile: '', invoiceEmail: '', poNumber: '', notes: '', reorderOf: null });
export function loadDraft(): Draft {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    /* fresh */
  }
  return emptyDraft();
}
export function saveDraft(d: Draft): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(d));
  } catch {
    /* private mode */
  }
}
/** After paying: the food, day and link go; the company and contact stay for next time. */
export const afterPaid = (d: Draft): Draft => ({ ...emptyDraft(), place: d.place, floor: d.floor, accessNotes: d.accessNotes, company: d.company, contactName: d.contactName, contactMobile: d.contactMobile, invoiceEmail: d.invoiceEmail, headcount: d.headcount });

export const allocKey = (f: Flavour, p: Protein): string => `${f}|${p}`;
export const placed = (g: { alloc: Record<string, number> } | undefined): number => Object.values(g?.alloc ?? {}).reduce((a, v) => a + v, 0);
export const newBowl = (items: CateringItem[], from?: Bowl): Bowl => ({ id: uid(), name: '', itemId: from?.itemId ?? items.find((i) => i.kind === 'meal')?.id ?? '', protein: from?.protein ?? 'chicken', flavour: from?.flavour ?? 'Classic', note: '' });

/** Spread a quantity over the flavours in turn (chicken), as the prototype's "Split evenly". */
export function evenSplit(qty: number, flavours: readonly Flavour[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (let i = 0; i < qty; i++) {
    const k = allocKey(flavours[i % flavours.length]!, 'chicken');
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

/** The lines the API prices: the same shape whichever way the buyer ordered. */
export function toLines(d: Draft, items: CateringItem[]): CateringLine[] {
  const lines: CateringLine[] = [];
  if (d.mode === 'group') {
    for (const [itemId, g] of Object.entries(d.group)) {
      if (!g.qty || !items.some((i) => i.id === itemId)) continue;
      const allocations: Allocation[] = Object.entries(g.alloc).filter(([, q]) => q > 0).map(([k, qty]) => {
        const [flavour, protein] = k.split('|') as [Flavour, Protein];
        return { flavour, protein, qty };
      });
      lines.push({ id: itemId, itemId, qty: g.qty, allocations });
    }
  } else if (d.mode === 'individual') {
    const byItem = new Map<string, Allocation[]>();
    for (const b of d.bowls) {
      if (!b.itemId) continue;
      const arr = byItem.get(b.itemId) ?? [];
      arr.push({ flavour: b.flavour, protein: b.protein, qty: 1, eaterName: b.name.trim() || null, dietaryNote: b.note.trim() || null });
      byItem.set(b.itemId, arr);
    }
    for (const [itemId, allocations] of byItem) lines.push({ id: `b_${itemId}`, itemId, qty: allocations.length, allocations });
  }
  for (const [itemId, qty] of Object.entries(d.extras)) if (qty > 0 && items.some((i) => i.id === itemId)) lines.push({ id: `x_${itemId}`, itemId, qty, allocations: [] });
  return lines;
}

/** The request body for a quote or an order. */
export function toRequest(d: Draft, items: CateringItem[]): Record<string, unknown> {
  return {
    dateKey: d.dateKey ?? '', method: d.method, slot: d.method === 'delivery' ? d.slot : null, mode: d.mode ?? 'group', headcount: d.headcount,
    lines: d.mode === 'team' ? [] : toLines(d, items), teamLinkToken: d.mode === 'team' ? d.team?.token ?? null : null,
    placeId: d.method === 'delivery' ? d.place?.placeId ?? null : null, floor: d.floor, accessNotes: d.accessNotes,
    company: d.company, contactName: d.contactName, contactMobile: d.contactMobile, invoiceEmail: d.invoiceEmail, poNumber: d.poNumber, notes: d.notes, reorderOf: d.reorderOf,
  };
}

/** Instant sums for the summary while the server's quote is on its way (the server's figures replace them). */
export function clientTotals(d: Draft, items: CateringItem[], teamLines: CateringLine[] | null): { rows: { name: string; qty: number; pence: number }[]; totalPence: number; mains: number; boxes: number } {
  const lines = d.mode === 'team' ? teamLines ?? [] : toLines(d, items);
  const rows: { name: string; qty: number; pence: number }[] = [];
  let totalPence = 0, mains = 0, boxes = 0;
  for (const l of lines) {
    const it = items.find((i) => i.id === l.itemId);
    if (!it) continue;
    rows.push({ name: it.name, qty: l.qty, pence: it.pricePence * l.qty });
    totalPence += it.pricePence * l.qty;
    if (it.kind === 'meal') {
      mains += l.qty;
      boxes += l.qty;
    } else if (it.kind === 'tray') {
      if (it.flavourChoice) mains += l.qty * it.serves;
      boxes += l.qty;
    }
  }
  return { rows, totalPence, mains, boxes };
}

/** What stops the order right now, in the buyer's words; the server's quote says the same (and more) once it answers. */
export function localIssue(d: Draft, items: CateringItem[], days: CateringDay[], teamLines: CateringLine[] | null, minimumPence: number): string {
  const day = days.find((x) => x.dateKey === d.dateKey);
  if (!day) return 'Pick a day';
  if (!day.open) return `Orders for ${day.label.toLowerCase()} have closed`;
  if (d.method === 'delivery' && !d.slot) return 'Pick a delivery time';
  if (d.method === 'delivery' && !d.place) return 'Add the delivery address';
  if (d.method === 'delivery' && d.place && !d.place.inZone) return 'That address is outside our delivery zone';
  if (!d.mode) return 'Choose how you want to order';
  const t = clientTotals(d, items, teamLines);
  if (d.mode === 'team' && !d.team) return 'Create the team link';
  if (!t.mains) return d.mode === 'individual' ? 'Add the first bowl' : 'Add some mains';
  if (d.mode === 'group') {
    for (const [itemId, g] of Object.entries(d.group)) {
      const it = items.find((i) => i.id === itemId);
      if (it?.flavourChoice && g.qty && placed(g) !== g.qty) return `Finish the flavour split for ${it.name}`;
    }
  }
  if (d.mode === 'individual' && d.bowls.some((b) => !b.name.trim())) return 'Add a name for every box';
  if (d.method === 'delivery' && t.totalPence < minimumPence) return `${fmtPence(minimumPence - t.totalPence)} more for delivery (${fmtPence(minimumPence)} minimum)`;
  if (!d.company.trim() || !d.contactName.trim() || !d.contactMobile.trim()) return 'Add the company and on-the-day contact';
  return '';
}
const fmtPence = (p: number) => `£${(p / 100).toFixed(2)}`;

/* ---------- calls ---------- */

export const cateringApi = {
  menu: () => api<CateringMenu>('/api/catering/menu'),
  days: () => api<{ now: string; days: CateringDay[] }>('/api/catering/days'),
  places: (q: string, session: string) => api<{ suggestions: Suggestion[]; configured: boolean }>(`/api/catering/places?q=${encodeURIComponent(q)}&session=${encodeURIComponent(session)}`),
  place: (id: string, session: string) => api<PlaceDetail>(`/api/catering/places/${encodeURIComponent(id)}?session=${encodeURIComponent(session)}`),
  quote: (body: Record<string, unknown>) => api<Quote>('/api/catering/quote', { body }),
  orders: () => api<{ orders: CateringView[] }>('/api/catering/orders'),
  order: (id: string) => api<CateringView>(`/api/catering/orders/${id}`),
  addresses: () => api<{ addresses: SavedAddress[] }>('/api/catering/addresses'),
  roster: () => api<{ entries: RosterEntry[] }>('/api/catering/roster'),
  teamLink: (token: string) => api<{ link: TeamLink }>(`/api/catering/team-links/${token}`),
};

/** "Book this again": a past order's food and details go back into the draft; the buyer picks a new day. */
export async function loadReorder(orderId: string): Promise<Draft> {
  const r = await api<{ draft: { lines: { itemId: string; qty: number; allocations: { protein: string | null; flavour: string | null; qty: number; eaterName: string | null; dietaryNote: string | null }[] }[]; method: CateringMethod; mode: CateringMode; headcount: number; floor: string; accessNotes: string; company: string; contactName: string; contactMobile: string; invoiceEmail: string; poNumber: string; notes: string; savedAddress: SavedAddress | null } }>(`/api/catering/orders/${orderId}/reorder`);
  const menu = await cateringApi.menu();
  const d: Draft = { ...emptyDraft(), method: r.draft.method, mode: r.draft.mode === 'team' ? 'group' : r.draft.mode, headcount: r.draft.headcount, floor: r.draft.floor, accessNotes: r.draft.accessNotes, company: r.draft.company, contactName: r.draft.contactName, contactMobile: r.draft.contactMobile, invoiceEmail: r.draft.invoiceEmail, poNumber: r.draft.poNumber, notes: r.draft.notes, reorderOf: orderId };
  for (const l of r.draft.lines) {
    const it = menu.items.find((i) => i.id === l.itemId);
    if (!it) continue;
    if (it.flavourChoice) {
      if (d.mode === 'individual') {
        for (const a of l.allocations) for (let i = 0; i < a.qty; i++) d.bowls.push({ id: uid(), name: a.eaterName ?? '', itemId: it.id, protein: (a.protein as Protein | null) ?? 'chicken', flavour: (a.flavour as Flavour | null) ?? 'Classic', note: a.dietaryNote ?? '' });
      } else {
        const alloc: Record<string, number> = {};
        for (const a of l.allocations) if (a.flavour && a.protein) alloc[`${a.flavour}|${a.protein}`] = (alloc[`${a.flavour}|${a.protein}`] ?? 0) + a.qty;
        d.group[it.id] = { qty: l.qty, alloc };
      }
    } else d.extras[it.id] = (d.extras[it.id] ?? 0) + l.qty;
  }
  if (r.draft.savedAddress) d.place = { placeId: r.draft.savedAddress.place_id, name: r.draft.savedAddress.name, address: r.draft.savedAddress.address, inZone: true, miles: 0, text: 'Saved address: we check the zone again at checkout' };
  saveDraft(d);
  return d;
}
